import { generatePath, Link } from "react-router-dom";

import {
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Stack,
  Pagination,
} from "@mui/material";
import { RemoveCircleOutline } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

import nameof from "ts-nameof.macro";

import AlertExtended from "../../../../../common/components/mui-extended/AlertExtended";
import SectionExtended from "../../../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../../../common/components/mui-extended/TextFieldExtended";
import { BankSearchDto, BankSearchResultDto } from "../../../../../interfaces/admin/Bank";
import { PageDefaults } from "../../../../../common/interfaces/PageDto";
import { useAxiosLoader } from "../../../../../common/hooks/useAxiosLoader";
import { useSearchForm } from "../../../../../common/hooks/useSearchForm";
import { AdminRoutesConfig } from "../../../../RoutesConfig";
import bankService from "../../../../../services/bankService";
import { translate } from "../../../../../common/utils/formatters";
import TableSkeleton from "../../../../../common/components/TableSkeleton";

const defaultModel: BankSearchDto = {
  ...PageDefaults,
};

export default function Search() {
  const loading = useAxiosLoader(false);

  const form = useSearchForm<BankSearchDto, BankSearchResultDto>({
    defaultModel: defaultModel,
    rootSearchPath: AdminRoutesConfig.Bank.Search.Path,
    searchAsync: async (query: string) => {
      await bankService.getBanksAsync(query).then(async ({ data }) => {
        await bankService.getBanksCountAsync(query).then(({ data: count }) => {
          form.setResults(data, count);
        });
      });
    },
  });

  const renderForm = () => {
    return (
      <form onReset={form.onReset} onSubmit={form.onSubmit}>
        <Stack spacing={3}>
          <SectionExtended title="Данни за банка">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextFieldExtended label="Име" name={nameof(defaultModel.name)} form={form} />
              </Grid>
            </Grid>
          </SectionExtended>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button startIcon={<RemoveCircleOutline />} type="reset">
                Изчисти
              </Button>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                <Button
                  component={Link}
                  to={AdminRoutesConfig.Bank.New.Path}
                  startIcon={AdminRoutesConfig.Bank.New.Icon}
                >
                  {AdminRoutesConfig.Bank.New.Label}
                </Button>
                <LoadingButton
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={AdminRoutesConfig.Bank.Search.Icon}
                  type="submit"
                >
                  {AdminRoutesConfig.Bank.Search.Label}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </form>
    );
  };

  const renderTable = () => {
    return loading ? (
      <TableSkeleton />
    ) : form.results && form.results.length > 0 ? (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="25%">Име</TableCell>
                <TableCell width="25%">Пореден номер</TableCell>
                <TableCell width="25%">Брой клонове</TableCell>
                <TableCell width="25%">Активен</TableCell>
                <TableCell width="90px"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.results.map((item) => (
                <TableRow key={item.bankId} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.order}</TableCell>
                  <TableCell>{item.bankBranchesCount}</TableCell>
                  <TableCell>{translate(item.isActive)}</TableCell>
                  <TableCell>
                    <Stack direction="row">
                      <Tooltip title={AdminRoutesConfig.Bank.Edit.Label}>
                        <IconButton
                          component={Link}
                          to={generatePath(AdminRoutesConfig.Bank.Edit.Path, {
                            id: item.bankId?.toString(),
                          })}
                        >
                          {AdminRoutesConfig.Bank.Edit.Icon}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={form.pageCount}
          onChange={form.onPageChanged}
          page={form.watch(nameof(defaultModel.currentPage))}
        />
      </>
    ) : (
      form.searched && <AlertExtended type="warning">Няма намерени резултати.</AlertExtended>
    );
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Банки</Typography>
      {renderForm()}
      {renderTable()}
    </Stack>
  );
}
